import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { DonutChartConfig } from '../../../models/chart-config.model';
import { DonutChartData } from '../../../models/chart-data.model';
import { ThemeConfig } from '../../../models/theme.model';
import { DataService } from '../../../services/data.service';
import {
	highchartsPaletteForThemeMode,
	pinHighchartsPaletteAndTooltipForThemeMode,
	pinHighchartsPaletteForThemeMode,
} from '@framework/analytics/utils/highcharts-palette-for-theme.util';
import {
	explicitChartTooltipHtmlTheme,
	resolveChartThemeMode,
} from '@framework/analytics/utils/resolve-chart-theme-mode.util';
import { PaletteService } from '../../../services/palette.service';
import { ThemeMode, ThemeService } from '../../../services/theme.service';
import Highcharts from '../highcharts.config';
import {
	createHighchartsResizeObserver,
	reflowHighchartsToContainer,
	resolveHighchartsCssColors,
	scheduleHighchartsReflowAfterLayout,
} from '../../../utils/resolve-highcharts-css-colors.util';
import { applyLegendLayoutToChartOptions } from '../../../utils/chart-legend-scale.util';
import { ChartRenderCoordinator } from '../../../utils/chart-render-coordinator.util';
import {
	createDonutChartTooltipFormatter,
	donutChartDefaults,
} from './jepto-donut-chart.defaults';

interface DonutChartDataPoint {
	name: string;
	y: number;
	color?: string;
}

interface ChartClickEvent {
	point: { name: string; value: number; percentage: number };
	chart: { type: string; id?: string };
}

/**
 * Donut Chart Component with signal-based reactivity
 */
type DonutChartCustomFlags = {
	disableLegendLayout?: boolean;
	simpleSideLegend?: boolean;
};

@Component({
	selector: 'jepto-donut-chart',
	templateUrl: './jepto-donut-chart.component.html',
	styleUrls: ['./jepto-donut-chart.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule],
	host: {
		class: 'jepto-donut-chart',
		'[class.jepto-donut-chart--side-legend]': 'useSideLegend()',
	},
})
export class JeptoDonutChartComponent implements AfterViewInit, OnDestroy {
	// Signal inputs
	cardId = input<string>();
	config = input<DonutChartConfig>();
	data = input<
		| DonutChartData
		| {
				data?: {
					donutSeries?: Array<{ name: string; value: number }>;
					total?: number;
					centerLabel?: { title: string; subtitle?: string };
				};
		  }
	>();
	themeMode = input<ThemeMode>('auto');
	palette = input<string>('pal_jepto');
	themeConfig = input<ThemeConfig>();
	height = input<string>('100%');
	width = input<string>('100%');
	showLegend = input<boolean>(true);
	enableTooltip = input<boolean>(true);
	innerRadius = input<string>('60%');

	// Outputs
	chartClick = output<ChartClickEvent>();
	sliceClick = output<ChartClickEvent>();

	// View queries
	private chartContainer = viewChild.required<ElementRef>('chartContainer');

	// Services
	private themeService = inject(ThemeService);
	private paletteService = inject(PaletteService);
	private dataService = inject(DataService, { optional: true });

	// Internal state
	private chart?: Highcharts.Chart;
	private resizeObserver?: ResizeObserver;
	private layoutObserver?: ResizeObserver;
	private initialized = false;
	private renderCoordinator = new ChartRenderCoordinator();

	// Computed signals
	private effectiveData = computed(() => {
		const direct = this.data();
		if (direct) return direct;

		const id = this.cardId();
		if (id && this.dataService) {
			const serviceData = this.dataService.cardDataSignal(id)();
			if (serviceData?.chartType === 'donut')
				return serviceData as unknown as DonutChartData;
		}
		return null;
	});

	// Default color palette fallback
	private readonly DEFAULT_COLORS = [
		'#651FFF',
		'#42A5F5',
		'#AB47BC',
		'#26C6DA',
		'#7E57C2',
		'#66BB6A',
		'#FFA726',
		'#EF5350',
	];

	private paletteColors = computed(() => {
		const cfg = this.config();
		const paletteId = cfg?.palette?.selectedPaletteId ?? this.palette();
		const resolvedMode = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);
		const colors = this.paletteService.getColorPalette(
			paletteId,
			resolvedMode
		);
		// Always return a valid array - use default if palette is empty
		return colors.length > 0 ? colors : this.DEFAULT_COLORS;
	});

	private chartData = computed(() => {
		const sourceData = this.effectiveData();
		if (
			!sourceData?.data?.donutSeries ||
			!Array.isArray(sourceData.data.donutSeries)
		)
			return [];
		return sourceData.data.donutSeries
			.filter(
				(segment): segment is { name: string; value: number } =>
					segment != null &&
					typeof segment === 'object' &&
					'name' in segment &&
					'value' in segment
			)
			.map(segment => ({ name: segment.name, y: segment.value }));
	});

	protected readonly centerLabel = computed(
		() => this.effectiveData()?.data?.centerLabel || null
	);

	/** HTML hole label — only for side-legend donuts (AI Visibility mock layout). */
	protected readonly htmlCenterLabel = computed(() => {
		if (!this.useSideLegend()) {
			return null;
		}
		return this.centerLabel();
	});

	private sliceColors = computed(() => {
		const cfg = this.config();
		const vizColors = cfg?.visualization?.colors;
		const paletteColorsArr = this.paletteColors();
		return Array.isArray(vizColors) && vizColors.length > 0
			? vizColors
			: paletteColorsArr;
	});

	protected readonly useSideLegend = computed(() => {
		const custom = (
			this.config()?.visualization?.chart as
				| { custom?: DonutChartCustomFlags }
				| undefined
		)?.custom;
		return custom?.simpleSideLegend === true;
	});

	protected readonly sideLegendItems = computed(() => {
		const colors = this.sliceColors();
		return this.chartData().map((segment, index) => ({
			name: segment.name,
			color: colors[index % colors.length] ?? '#651fff',
		}));
	});

	private total = computed(() => {
		const sourceData = this.effectiveData();
		if (sourceData?.data?.total !== undefined) return sourceData.data.total;
		return this.chartData().reduce((sum, segment) => sum + segment.y, 0);
	});

	private chartOptions = computed(() => {
		const baseOptions = { ...donutChartDefaults };
		const cfg = this.config();
		const vizColors = cfg?.visualization?.colors;
		const paletteColorsArr = this.paletteColors();
		const colors =
			Array.isArray(vizColors) && vizColors.length > 0
				? vizColors
				: paletteColorsArr;
		const id = this.cardId();
		const innerRadius = this.innerRadius();
		const centerLabel = this.centerLabel();
		const sideLegend = this.useSideLegend();
		const highchartsHoleLabel = centerLabel && !sideLegend;

		const themeModeInput = this.themeMode();
		const pinnedHtmlTheme = explicitChartTooltipHtmlTheme(themeModeInput);
		const resolvedThemeMode = resolveChartThemeMode(
			themeModeInput,
			this.themeService.getActiveThemeModeSignal()()
		);

		const componentOptions: Partial<Highcharts.Options> = {
			...highchartsPaletteForThemeMode(resolvedThemeMode),
			colors: colors?.length ? colors : paletteColorsArr, // Always provide a valid array (never undefined)
			chart: {
				...baseOptions.chart,
				backgroundColor: 'var(--chart-background, transparent)',
			},
			title: highchartsHoleLabel
				? {
						text: centerLabel.title,
						floating: true,
						verticalAlign: 'middle',
						y: centerLabel.subtitle ? -12 : 0,
						style: {
							color: 'var(--chart-text-color, inherit)',
							fontSize: 'var(--font-size-2xl, 24px)',
							fontWeight: '500',
						},
					}
				: {
						text: cfg?.title || '',
						style: { color: 'var(--chart-text-color, inherit)' },
					},
			subtitle: highchartsHoleLabel
				? {
						text: centerLabel.subtitle ?? '',
						floating: true,
						verticalAlign: 'middle',
						y: 12,
						style: {
							color: 'var(--text-secondary-color, inherit)',
							fontSize: 'var(--font-size-sm, 14px)',
						},
					}
				: {
						text: cfg?.subtitle || '',
						style: { color: 'var(--chart-text-color, inherit)' },
					},
			legend: { ...baseOptions.legend, enabled: this.showLegend() },
			tooltip: {
				...baseOptions.tooltip,
				enabled: this.enableTooltip(),
				formatter: createDonutChartTooltipFormatter(pinnedHtmlTheme),
			},
			plotOptions: {
				...baseOptions.plotOptions,
				pie: {
					...baseOptions.plotOptions?.pie,
					innerSize: innerRadius,
					point: {
						events: {
							click: (event: any) => {
								const point = event.point;
								this.chartClick.emit({
									point: {
										name: point.name,
										value: point.y,
										percentage: point.percentage,
									},
									chart: { type: 'donut', id },
								});
								this.sliceClick.emit({
									point: {
										name: point.name,
										value: point.y,
										percentage: point.percentage,
									},
									chart: { type: 'donut', id },
								});
							},
						},
					},
				},
			},
			series: [
				{
					type: 'pie' as const,
					name: cfg?.title || 'Distribution',
					data: this.chartData(),
				},
			],
		};

		let merged = Highcharts.merge(baseOptions, componentOptions);
		if (cfg?.visualization)
			merged = Highcharts.merge(merged, cfg.visualization);
		if (sideLegend && centerLabel) {
			merged = Highcharts.merge(merged, {
				title: { text: undefined },
				subtitle: { text: undefined },
			});
		}
		if (pinnedHtmlTheme) {
			return pinHighchartsPaletteAndTooltipForThemeMode(
				merged,
				resolvedThemeMode,
				createDonutChartTooltipFormatter(pinnedHtmlTheme)
			);
		}
		return pinHighchartsPaletteForThemeMode(merged, resolvedThemeMode);
	});

	constructor() {
		effect(() => {
			const data = this.chartData();
			const options = this.chartOptions();
			if (!this.initialized) return;

			const container = this.chartContainer();
			if (!container || data.length === 0) return;

			const containerEl = container.nativeElement;
			this.renderCoordinator.schedule(() => {
				if (this.chart) {
					this.chart.destroy();
					this.chart = undefined;
				}
				this.renderChart(options);
			});
		});
	}

	ngAfterViewInit(): void {
		this.initializeChart();
		this.setupResizeObserver();
		this.initialized = true;
	}

	ngOnDestroy(): void {
		this.renderCoordinator.cancel();
		this.resizeObserver?.disconnect();
		this.layoutObserver?.disconnect();
		this.chart?.destroy();
	}

	private initializeChart(): void {
		this.tryRenderChart();
	}

	private tryRenderChart(): void {
		const container = this.chartContainer();
		const data = this.chartData();
		if (!container || data.length === 0) {
			return;
		}
		const el = container.nativeElement;
		if (el.clientWidth === 0 && el.clientHeight === 0) {
			return;
		}
		if (this.chart) {
			reflowHighchartsToContainer(this.chart, el);
			return;
		}
		this.renderChart(this.chartOptions());
	}

	private renderChart(options: Highcharts.Options): void {
		const container = this.chartContainer();
		if (!container) {
			return;
		}
		const el = container.nativeElement;
		const resolved = resolveHighchartsCssColors(
			applyLegendLayoutToChartOptions(options, el),
			el
		);
		this.chart = Highcharts.chart(el, resolved);
		reflowHighchartsToContainer(this.chart, el);
		scheduleHighchartsReflowAfterLayout(() => this.chart, el);
	}

	private setupResizeObserver(): void {
		this.resizeObserver?.disconnect();
		this.layoutObserver?.disconnect();
		const el = this.chartContainer().nativeElement;
		this.resizeObserver = createHighchartsResizeObserver(
			() => this.chart,
			el
		);
		const plot = el.closest('.jepto-donut-chart-plot') as HTMLElement | null;
		if (this.resizeObserver && plot) {
			this.resizeObserver.observe(plot);
		}

		if (typeof ResizeObserver !== 'undefined' && plot) {
			this.layoutObserver = new ResizeObserver(() => {
				if (!this.initialized || this.chart) {
					return;
				}
				const container = this.chartContainer()?.nativeElement;
				if (!container || this.chartData().length === 0) {
					return;
				}
				if (
					container.clientWidth === 0 &&
					container.clientHeight === 0
				) {
					return;
				}
				this.tryRenderChart();
			});
			this.layoutObserver.observe(plot);
		}
	}
}
